import { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, LineElement, PointElement, LineController, BarController } from 'chart.js';
import { Doughnut, Pie } from 'react-chartjs-2';
import API from '../api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, LineElement, PointElement, LineController, BarController);

const fmt      = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const fmtShort = v => { const n = Math.abs(v || 0); return n >= 1e6 ? '$' + (v/1e6).toFixed(1) + 'M' : n >= 1e3 ? '$' + (v/1e3).toFixed(0) + 'K' : fmt(v); };

const MESES_ABREV = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
const MESES_FULL  = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const currentYear = new Date().getFullYear();
const ANIO_INICIAL = 2019;
const ANIOS = Array.from({ length: currentYear - ANIO_INICIAL + 1 }, (_, i) => currentYear - i);

const hoy = new Date();
const MES_ANT_LABEL = MESES_ABREV[hoy.getMonth()] + ' ' + (hoy.getFullYear() - 1);

const ESTADO_STYLE = {
  PAGADO:    { bg: 'rgba(0,184,227,0.08)',   color: '#00b8e3' },
  FACTURADO: { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  PENDIENTE: { bg: 'rgba(251,191,36,0.08)',  color: '#b45309' },
};

const lineOnTopPlugin = {
  id: 'lineOnTop',
  afterDatasetsDraw(chart) {
    chart.data.datasets.forEach((dataset, i) => {
      if (dataset.type === 'line') {
        chart.getDatasetMeta(i).controller.draw();
      }
    });
  }
};

const chartOpts = {
  responsive: true,
  plugins: { legend: { position: 'bottom', labels: { font: { family: 'Space Grotesk', size: 11 }, color: '#5a7a8a', padding: 12, boxWidth: 12 } } },
};

function pct(actual, ant) {
  if (!ant || ant <= 0) return null;
  return Math.round((actual - ant) / ant * 100);
}

function mesLabel() {
  return MESES_FULL[hoy.getMonth()] + ' ' + hoy.getFullYear();
}

export default function Dashboard() {
  const [data,    setData]    = useState(null);
  const [dataAnt, setDataAnt] = useState(null);
  const [dataAnt2, setDataAnt2] = useState(null);
  const [anio,    setAnio]    = useState(currentYear);
  const [incluyeAcademia, setIncluyeAcademia] = useState(
    () => localStorage.getItem('dash_academia') === '1'
  );
  const [incluyeTrading, setIncluyeTrading] = useState(
    () => localStorage.getItem('dash_trading') === '1'
  );
  const [oculto, setOculto] = useState(
    () => localStorage.getItem('dash_oculto') !== '0'
  );
  const [acadTotal, setAcadTotal] = useState(null);
  const [acadAnio, setAcadAnio] = useState(null);
  const [ibcMes,   setIbcMes]   = useState(hoy.getMonth() + 1);
  const [utilidadChartYears, setUtilidadChartYears] = useState(
    () => [currentYear, currentYear - 1].filter(y => y >= ANIO_INICIAL)
  );
  const [utilidadChartValor, setUtilidadChartValor] = useState('ambos');
  const [metricasUtilidad, setMetricasUtilidad] = useState({});

  useEffect(() => {
    API.get(`/metricas?anio=${anio}`).then(r => setData(r.data));
    API.get(`/metricas?anio=${anio - 1}`).then(r => setDataAnt(r.data)).catch(() => setDataAnt(null));
    API.get(`/metricas?anio=${anio - 2}`).then(r => setDataAnt2(r.data)).catch(() => setDataAnt2(null));
  }, [anio]);

  useEffect(() => {
    let activo = true;
    Promise.all(
      utilidadChartYears.map(y =>
        API.get(`/metricas?anio=${y}`)
          .then(r => [y, r.data])
          .catch(() => [y, null])
      )
    ).then(entries => {
      if (!activo) return;
      setMetricasUtilidad(Object.fromEntries(entries.filter(([, value]) => value)));
    });
    return () => { activo = false; };
  }, [utilidadChartYears]);

  useEffect(() => {
    if (incluyeAcademia) {
      API.get(`/academia/resumen?mes=${encodeURIComponent(mesLabel())}`)
        .then(r => setAcadTotal(r.data)).catch(() => setAcadTotal(null));
    } else {
      setAcadTotal(null);
    }
  }, [incluyeAcademia]);

  // Acumulado del año para los indicadores anuales. Sin esto la academia solo
  // sumaba en el mes y quedaba por fuera de la utilidad neta del año.
  useEffect(() => {
    if (incluyeAcademia) {
      API.get(`/academia/resumen?anio=${anio}`)
        .then(r => setAcadAnio(r.data)).catch(() => setAcadAnio(null));
    } else {
      setAcadAnio(null);
    }
  }, [incluyeAcademia, anio]);

  const toggleAcademia = () => {
    const next = !incluyeAcademia;
    setIncluyeAcademia(next);
    localStorage.setItem('dash_academia', next ? '1' : '0');
  };
  const toggleTrading = () => {
    const next = !incluyeTrading;
    setIncluyeTrading(next);
    localStorage.setItem('dash_trading', next ? '1' : '0');
  };
  const toggleOculto = () => {
    const next = !oculto;
    setOculto(next);
    localStorage.setItem('dash_oculto', next ? '1' : '0');
  };
  const fmtM   = v => oculto ? '$ ••••' : fmt(v);
  const fmtPct = s => oculto ? '••%' : s;
  const toggleUtilidadYear = year => {
    setUtilidadChartYears(prev => {
      const existe = prev.includes(year);
      if (existe && prev.length === 1) return prev;
      const next = existe ? prev.filter(y => y !== year) : [...prev, year];
      return next.sort((a, b) => b - a);
    });
  };

  if (!data) return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: '#5a7a8a', fontSize: 13, letterSpacing: '0.06em' }}>CARGANDO...</div>
  );

  const estadoMap = {};
  data.porEstado.forEach(e => { estadoMap[e.estado] = e; });

  const academiaIngreso = incluyeAcademia && acadTotal ? parseFloat(acadTotal.pagado) || 0 : 0;
  const tradingMesResultado = incluyeTrading ? (parseFloat(data.trading?.mes?.resultado) || 0) : 0;
  const tradingAnioResultado = incluyeTrading ? (parseFloat(data.trading?.anio?.resultado) || 0) : 0;
  const ingresosMostrar  = (parseFloat(data.ingresos)  || 0) + academiaIngreso;

  const deltaIngresos = pct(data.ingresos, data.mes_anterior?.ingresos);

  // Metas
  const esAnioActual  = parseInt(anio) === hoy.getFullYear();
  const mesNumActual  = hoy.getMonth() + 1;
  const mesActualData = data.porMes.find(m => Number(m.num_mes) === mesNumActual) || {};
  const utilidadPropiaActual = parseFloat(data.utilidad) || 0;
  const noFacturoUtilidadMes = parseFloat(mesActualData.no_facturo_utilidad_total) || 0;
  const utilidadTotalMes = m => (parseFloat(m?.utilidad) || 0) + (parseFloat(m?.no_facturo_utilidad_total) || 0);
  const utilidadTotalActual = utilidadPropiaActual + noFacturoUtilidadMes;
  const utilidadNetaBase = utilidadTotalActual - (parseFloat(data.gastos_mes) || 0);
  const utilidadNetaMostrar = utilidadNetaBase + academiaIngreso + tradingMesResultado;
  const mesesAnt = esAnioActual
    ? data.porMes.filter(m => Number(m.num_mes) !== mesNumActual)
    : data.porMes;
  const promedioAnt = mesesAnt.length > 0
    ? mesesAnt.reduce((s, m) => s + utilidadTotalMes(m), 0) / mesesAnt.length
    : (parseFloat(data.mes_anterior?.utilidad) || 0);
  const meta1Label    = mesesAnt.length > 0 ? 'Superar promedio de meses anteriores' : `Superar ${MES_ANT_LABEL}`;
  const mesesParaRecord = esAnioActual ? mesesAnt : data.porMes;
  const mejorMes      = mesesParaRecord.reduce((max, m) => Math.max(max, utilidadTotalMes(m)), 0);
  const utilidadActual = utilidadTotalActual;
  const pctMeta1      = promedioAnt > 0 ? Math.min(Math.round(utilidadActual / promedioAnt * 100), 100) : 0;
  const pctMeta2      = mejorMes > 0   ? Math.min(Math.round(utilidadActual / mejorMes * 100), 100)   : 0;
  const faltaMeta1    = Math.max(promedioAnt - utilidadActual, 0);
  const faltaMeta2    = Math.max(mejorMes - utilidadActual, 0);

  // Gráfica mensual — incluye año anterior si existe
  const labels = MESES_ABREV.slice(0, esAnioActual ? hoy.getMonth() + 1 : 12);
  const getMes = (porMes, numMes) => {
    const m = porMes.find(x => Number(x.num_mes) === numMes);
    return { valor: parseFloat(m?.valor) || 0, utilidad: parseFloat(m?.utilidad) || 0, gastos: parseFloat(m?.gastos) || 0,
             no_facturo_total: parseFloat(m?.no_facturo_total) || 0,
             no_facturo_utilidad_total: parseFloat(m?.no_facturo_utilidad_total) || 0,
             sin_factura_total: parseFloat(m?.sin_factura_total) || 0 };
  };
  const mesActualIdx = esAnioActual ? hoy.getMonth() : -1;
  // gastosMap incluye gastos_fijos para todos los meses (retornado por backend como gastosPorMes)
  const getGastosMes = (numMes) => parseFloat(data.gastosPorMes?.[numMes]) || parseFloat(getMes(data.porMes, numMes).gastos) || 0;
  const totalGastosAnio = labels.reduce((s, _, i) => s + getGastosMes(i + 1), 0);
  const academiaAnioIngreso = incluyeAcademia && acadAnio ? parseFloat(acadAnio.pagado) || 0 : 0;
  const hayGastos = totalGastosAnio > 0;
  const barDatasets = [
    {
      label: `Ingresos propios ${anio}`,
      data: labels.map((_, i) => getMes(data.porMes, i+1).valor + (i === mesActualIdx ? academiaIngreso : 0)),
      backgroundColor: '#00b8e3', order: 1
    },
    {
      label: `Ingresos no facturo ${anio}`,
      data: labels.map((_, i) => getMes(data.porMes, i+1).no_facturo_total),
      backgroundColor: 'rgba(180,83,9,0.35)', order: 1
    },
    {
      label: hayGastos ? `Utilidad neta total ${anio}` : `Utilidad total ${anio}`,
      data: labels.map((_, i) => {
        const m = getMes(data.porMes, i+1);
        const utilidadTotal = m.utilidad + m.no_facturo_utilidad_total;
        const u = hayGastos ? (utilidadTotal - getGastosMes(i+1)) : utilidadTotal;
        return u + (i === mesActualIdx ? academiaIngreso : 0);
      }),
      backgroundColor: 'rgba(0,184,227,0.3)', order: 1
    },
    ...(hayGastos ? [{
      label: `Gastos ${anio}`,
      data: labels.map((_, i) => getGastosMes(i+1)),
      backgroundColor: 'rgba(239,68,68,0.5)', order: 1
    }] : []),
  ];
  if (dataAnt?.porMes?.length) {
    const getGastosAnt = (numMes) => parseFloat(dataAnt.gastosPorMes?.[numMes]) || parseFloat(getMes(dataAnt.porMes, numMes).gastos) || 0;
    const hayGastosAnt = Object.values(dataAnt.gastosPorMes || {}).some(v => parseFloat(v) > 0) || dataAnt.porMes.some(m => parseFloat(m.gastos) > 0);
    barDatasets.push({
      label: hayGastosAnt ? `Utilidad neta total ${anio-1}` : `Utilidad total ${anio-1}`,
      data: labels.map((_, i) => {
        const m = getMes(dataAnt.porMes, i+1);
        return (m.utilidad + m.no_facturo_utilidad_total) - getGastosAnt(i+1);
      }),
      borderColor: '#08293f',
      backgroundColor: 'transparent',
      borderWidth: 2.5,
      type: 'line',
      fill: false,
      tension: 0.3,
      pointRadius: 5,
      pointBackgroundColor: '#fff',
      pointBorderColor: '#08293f',
      pointBorderWidth: 2,
      order: 0
    });
  }
  const barData = { labels, datasets: barDatasets };

  const utilidadChartColors = ['#059669', '#00b8e3', '#6366f1', '#b45309', '#dc2626', '#08293f'];
  const utilidadChartLabels = MESES_ABREV;
  const utilidadChartRows = utilidadChartYears
    .map(year => ({ year, data: Number(year) === Number(anio) ? data : metricasUtilidad[year] }))
    .filter(row => row.data?.porMes?.length);
  const utilidadChartDatasets = utilidadChartRows.flatMap((row, idx) => {
    const color = utilidadChartColors[idx % utilidadChartColors.length];
    const valores = utilidadChartLabels.map((_, i) => utilidadTotalMes(getMes(row.data.porMes, i + 1)));
    const valoresConDatos = valores.filter(v => v > 0);
    const promedio = valoresConDatos.length ? valoresConDatos.reduce((s, v) => s + v, 0) / valoresConDatos.length : 0;
    const datasets = [];
    if (utilidadChartValor === 'bruta' || utilidadChartValor === 'ambos') {
      datasets.push({
        label: `Utilidad bruta ${row.year}`,
        data: valores,
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        type: 'line',
        fill: false,
        tension: 0.32,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#fff',
        pointBorderColor: color,
        pointBorderWidth: 2,
        order: 1
      });
    }
    if (utilidadChartValor === 'promedio' || utilidadChartValor === 'ambos') {
      datasets.push({
        label: `Promedio ${row.year}`,
        data: utilidadChartLabels.map(() => promedio),
        borderColor: color,
        backgroundColor: 'transparent',
        borderDash: utilidadChartValor === 'ambos' ? [6, 4] : [],
        borderWidth: 2.5,
        type: 'line',
        fill: false,
        tension: 0,
        pointRadius: utilidadChartValor === 'ambos' ? 0 : 3,
        pointBackgroundColor: '#fff',
        pointBorderColor: color,
        pointBorderWidth: 2,
        order: 0
      });
    }
    return datasets;
  });
  const utilidadComparativaData = { labels: utilidadChartLabels, datasets: utilidadChartDatasets };

  const clienteData = {
    labels: data.porCliente.slice(0,5).map(c => c.cliente),
    datasets: [{ data: data.porCliente.slice(0,5).map(c => c.valor),
      backgroundColor: ['#00b8e3','#08293f','#00d4ff','rgba(0,184,227,0.4)','#5a7a8a'], borderWidth: 0 }]
  };
  const tipoData = {
    labels: data.porTipo.map(t => t.tipo || 'Sin tipo'),
    datasets: [{ data: data.porTipo.map(t => t.valor), backgroundColor: ['#08293f','#00b8e3'], borderWidth: 0 }]
  };

  // Comparativo anual
  const antPorMes = dataAnt?.porMes || [];
  const ant2PorMes = dataAnt2?.porMes || [];
  const mesesComparativo = esAnioActual ? mesNumActual : 12;
  const filtrarPeriodoComparativo = rows => rows.filter(m => Number(m.num_mes) <= mesesComparativo);
  const actCompPorMes = filtrarPeriodoComparativo(data.porMes);
  const antCompPorMes = filtrarPeriodoComparativo(antPorMes);
  const ant2CompPorMes = filtrarPeriodoComparativo(ant2PorMes);
  const ingresoTotalMes = m => (parseFloat(m?.valor) || 0) + (parseFloat(m?.no_facturo_total) || 0);
  const totalAnt  = antCompPorMes.reduce((s, m) => s + ingresoTotalMes(m), 0);
  const utilAnt   = antCompPorMes.reduce((s, m) => s + utilidadTotalMes(m), 0);
  const totalAnt2 = ant2CompPorMes.reduce((s, m) => s + ingresoTotalMes(m), 0);
  const utilAnt2  = ant2CompPorMes.reduce((s, m) => s + utilidadTotalMes(m), 0);
  const totalAct  = actCompPorMes.reduce((s, m) => s + ingresoTotalMes(m), 0);
  const utilAct   = actCompPorMes.reduce((s, m) => s + utilidadTotalMes(m), 0);
  const mejorMesAct = actCompPorMes.reduce((best, m) => utilidadTotalMes(m) > utilidadTotalMes(best) ? m : best, null);
  const mejorMesAnt = antCompPorMes.reduce((best, m) => utilidadTotalMes(m) > utilidadTotalMes(best) ? m : best, null);
  const mejorMesAnt2 = ant2CompPorMes.reduce((best, m) => utilidadTotalMes(m) > utilidadTotalMes(best) ? m : best, null);
  const mesesConDatosAct = actCompPorMes.filter(m => utilidadTotalMes(m) > 0).length;
  const mesesConDatosAnt = antCompPorMes.filter(m => utilidadTotalMes(m) > 0).length;
  const mesesConDatosAnt2 = ant2CompPorMes.filter(m => utilidadTotalMes(m) > 0).length;
  const promedioAct = mesesConDatosAct > 0 ? utilAct / mesesConDatosAct : 0;
  const promedioAntVal = mesesConDatosAnt > 0 ? utilAnt / mesesConDatosAnt : 0;
  const promedioAnt2Val = mesesConDatosAnt2 > 0 ? utilAnt2 / mesesConDatosAnt2 : 0;
  const hayComparativo = antPorMes.length > 0 && totalAnt > 0;
  const facturadoSS    = data.porMes.reduce((s, m) => s + (parseFloat(m.facturado_ss_total) || Math.max(0, (parseFloat(m.valor) || 0) - (parseFloat(m.sin_factura_total) || 0))), 0);
  const utilAntFull = antPorMes.reduce((s, m) => s + utilidadTotalMes(m), 0);
  const proyeccionUtilAnio = promedioAct * 12;
  const diffProyeccionVsAnt = proyeccionUtilAnio - utilAntFull;
  const varDisplay = (actual, anterior) => {
    const varPct = pct(actual, anterior);
    if (varPct === null) return null;
    const diferencia = (parseFloat(actual) || 0) - (parseFloat(anterior) || 0);
    const signo = diferencia >= 0 ? '+' : '-';
    return `${varPct >= 0 ? '↑' : '↓'} ${Math.abs(varPct)}% (${signo}${fmtM(Math.abs(diferencia))})`;
  };
  const diffDisplay = (actual, anterior) => {
    const diferencia = (parseFloat(actual) || 0) - (parseFloat(anterior) || 0);
    const signo = diferencia >= 0 ? '+' : '-';
    return `${signo}${fmtM(Math.abs(diferencia))}`;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#08293f', margin: 0 }}>Dashboard</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Toggle ocultar valores */}
          <button onClick={toggleOculto} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20,
            border: `1px solid ${oculto ? '#6366f1' : '#e8eef2'}`,
            background: oculto ? 'rgba(99,102,241,0.08)' : '#fff',
            color: oculto ? '#6366f1' : '#5a7a8a',
            fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>
            <span style={{ width: 28, height: 14, borderRadius: 7, background: oculto ? '#6366f1' : '#e8eef2', display: 'inline-flex', alignItems: 'center', padding: '0 2px', transition: 'background 0.2s' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', marginLeft: oculto ? 'auto' : 0, transition: 'margin 0.2s', display: 'block' }} />
            </span>
            {oculto ? '🔒 Oculto' : '👁 Visible'}
          </button>
          {/* Toggle academia */}
          <button onClick={toggleAcademia} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20,
            border: `1px solid ${incluyeAcademia ? '#059669' : '#e8eef2'}`,
            background: incluyeAcademia ? 'rgba(5,150,105,0.08)' : '#fff',
            color: incluyeAcademia ? '#059669' : '#5a7a8a',
            fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>
            <span style={{ width: 28, height: 14, borderRadius: 7, background: incluyeAcademia ? '#059669' : '#e8eef2', display: 'inline-flex', alignItems: 'center', padding: '0 2px', transition: 'background 0.2s' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', marginLeft: incluyeAcademia ? 'auto' : 0, transition: 'margin 0.2s', display: 'block' }} />
            </span>
            Academia
          </button>
          <button onClick={toggleTrading} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20,
            border: `1px solid ${incluyeTrading ? (Number(data.trading?.mes?.resultado || 0) >= 0 ? '#059669' : '#dc2626') : '#e8eef2'}`,
            background: incluyeTrading ? (Number(data.trading?.mes?.resultado || 0) >= 0 ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)') : '#fff',
            color: incluyeTrading ? (Number(data.trading?.mes?.resultado || 0) >= 0 ? '#059669' : '#dc2626') : '#5a7a8a',
            fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>
            <span style={{ width: 28, height: 14, borderRadius: 7, background: incluyeTrading ? (Number(data.trading?.mes?.resultado || 0) >= 0 ? '#059669' : '#dc2626') : '#e8eef2', display: 'inline-flex', alignItems: 'center', padding: '0 2px', transition: 'background 0.2s' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', marginLeft: incluyeTrading ? 'auto' : 0, transition: 'margin 0.2s', display: 'block' }} />
            </span>
            Trading
          </button>
          <select value={anio} onChange={e => setAnio(e.target.value)} className="input" style={{ width: 'auto', fontSize: 12 }}>
            {ANIOS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* ── Alerta CDTs ── */}
      {data.cdts_alerta?.length > 0 && (
        <div style={{ marginBottom: 14, padding: '10px 16px', borderRadius: 2,
          background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.2)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#dc2626', marginBottom: 8 }}>⚠ CDTs próximos a vencer</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.cdts_alerta.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                background: '#fff', padding: '5px 12px', borderRadius: 2, border: '1px solid rgba(220,38,38,0.15)' }}>
                <span style={{ fontWeight: 700, color: '#08293f', fontSize: 12 }}>{c.entidad}</span>
                <span style={{ fontSize: 11, color: '#5a7a8a' }}>{fmtM(c.monto)}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: c.dias_restantes <= 7 ? '#dc2626' : '#b45309' }}>
                  {c.dias_restantes === 0 ? '¡Hoy!' : `${c.dias_restantes}d`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── KPIs anuales ── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 8 }}>— Acumulado {anio}</div>
        <div className="anual-grid">
          {[
            {
              label: 'Ingresos registrados',
              value: fmtM(totalAct),
              accent: true,
              sub: hayComparativo ? fmtPct(`${pct(totalAct, totalAnt) >= 0 ? '↑' : '↓'} ${Math.abs(pct(totalAct, totalAnt))}% vs ${anio-1}`) : null,
              subColor: hayComparativo ? (pct(totalAct, totalAnt) >= 0 ? '#00b8e3' : '#f87171') : null,
            },
            {
              label: 'Utilidad bruta total',
              value: fmtM(utilAct),
              color: '#059669',
              accentGreen: true,
              sub: hayComparativo ? fmtPct(`${pct(utilAct, utilAnt) >= 0 ? '↑' : '↓'} ${Math.abs(pct(utilAct, utilAnt))}% vs ${anio-1}`) : null,
              subColor: hayComparativo ? (pct(utilAct, utilAnt) >= 0 ? '#059669' : '#dc2626') : null,
            },
            {
              label: 'Margen bruto promedio',
              value: oculto ? '••%' : (totalAct > 0 ? Math.round(utilAct / totalAct * 100) + '%' : '—'),
              color: '#6366f1',
              sub: data.porMes.filter(m => parseFloat(m.valor) > 0).length + ' meses con datos',
            },
            ...(hayGastos ? [
              {
                label: 'Gastos totales',
                value: oculto ? '••••' : fmtM(totalGastosAnio),
                color: '#ef4444',
                sub: 'nómina + operativos',
              },
              {
                label: incluyeAcademia || incluyeTrading ? 'Utilidad neta total ajustada' : 'Utilidad neta total',
                value: oculto ? '••••' : fmtM(utilAct - totalGastosAnio + tradingAnioResultado + academiaAnioIngreso),
                color: (utilAct - totalGastosAnio + tradingAnioResultado + academiaAnioIngreso) >= 0 ? '#059669' : '#ef4444',
                sub: oculto ? '••%'
                  : [
                      academiaAnioIngreso ? `Academia: +${fmtM(academiaAnioIngreso)}` : null,
                      tradingAnioResultado ? `Trading: ${tradingAnioResultado >= 0 ? '+' : '-'}${fmtM(Math.abs(tradingAnioResultado))}` : null,
                    ].filter(Boolean).join(' · ')
                    || (totalAct > 0 ? Math.round((utilAct - totalGastosAnio) / totalAct * 100) + '% margen neto' : ''),
              },
            {
              label: 'Facturado al año',
              value: oculto ? '••••' : fmtM(facturadoSS),
              color: '#0284c7',
              sub: 'base SS · no fact. y sin factura deducidos',
            },
            ] : []),
            ...(hayComparativo ? [
              {
                label: `Proyección utilidad ${anio}`,
                value: oculto ? '••••' : fmtM(proyeccionUtilAnio),
                color: diffProyeccionVsAnt >= 0 ? '#059669' : '#dc2626',
                sub: oculto ? 'vs cierre anterior' : `${diffProyeccionVsAnt >= 0 ? '+' : '-'}${fmtM(Math.abs(diffProyeccionVsAnt))} vs cierre ${anio - 1}`,
              },
            ] : []),
            ...(incluyeTrading ? [{
              label: 'Trading año',
              value: fmtM(data.trading?.anio?.resultado || 0),
              color: Number(data.trading?.anio?.resultado || 0) >= 0 ? '#059669' : '#dc2626',
              sub: `${data.trading?.anio?.operaciones || 0} ops · ${oculto ? '••%' : Number(data.trading?.anio?.rendimiento_pct || 0).toFixed(2) + '%'}`,
            }] : []),
          ].map((k, i) => (
            <div key={i} style={{
              background: k.accent ? '#08293f' : k.accentGreen ? 'rgba(5,150,105,0.05)' : '#fff',
              border: k.accent ? '1px solid rgba(0,184,227,0.3)'
                    : k.accentGreen ? '1px solid rgba(5,150,105,0.25)'
                    : '1px solid #e8eef2',
              padding: '14px 16px', borderRadius: 2
            }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: k.accent ? 'rgba(0,184,227,0.6)' : '#5a7a8a', marginBottom: 5 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1,
                color: k.accent ? '#00b8e3' : (k.color || '#08293f') }}>{k.value}</div>
              {k.sub && (
                <div style={{ fontSize: 10, fontWeight: 600, marginTop: 4, color: k.subColor || (k.accent ? 'rgba(0,184,227,0.5)' : '#b3b3b3') }}>{k.sub}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── KPIs mes ── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 8 }}>— Mes actual</div>
        <div className="kpi-grid">
          {[
            {
              label: incluyeAcademia ? 'Ingresos propios + Academia' : 'Ingresos propios',
              value: fmtM(ingresosMostrar),
              accent: true,
              delta: deltaIngresos,
              sub: incluyeAcademia && academiaIngreso > 0
                ? `Academia: ${fmtM(academiaIngreso)}`
                : deltaIngresos !== null ? `vs ${MES_ANT_LABEL}` : 'este mes'
            },
            {
              label: 'Utilidad bruta total',
              value: fmtM(utilidadTotalActual),
              color: '#059669',
              sub: noFacturoUtilidadMes > 0
                ? `Propia: ${fmtM(utilidadPropiaActual)} · Comisión/no facturo: ${fmtM(noFacturoUtilidadMes)}`
                : 'sin comisiones no facturo'
            },
            { label: 'Gastos mes',    value: fmtM(data.gastos_mes), color: '#6366f1' },
            {
              label: incluyeAcademia || incluyeTrading ? 'Utilidad neta total ajustada' : 'Utilidad neta total',
              value: fmtM(utilidadNetaMostrar),
              color: utilidadNetaMostrar >= 0 ? '#059669' : '#dc2626',
              accentGreen: utilidadNetaMostrar >= 0,
                sub: [
                  incluyeAcademia && academiaIngreso > 0 ? `Academia: +${fmtM(academiaIngreso)}` : null,
                  incluyeTrading && tradingMesResultado ? `Trading: ${tradingMesResultado >= 0 ? '+' : '-'}${fmtM(Math.abs(tradingMesResultado))}` : null,
                  !incluyeAcademia && !incluyeTrading && noFacturoUtilidadMes > 0 ? `Incluye comisión/no facturo: ${fmtM(noFacturoUtilidadMes)}` : null,
                ].filter(Boolean).join(' · ') || undefined,
            },
            { label: 'IVA',        value: fmtM(data.iva),        color: '#b45309' },
            { label: 'Cartera', value: fmtM(data.cartera), color: '#dc2626', sub: 'por cobrar año' },
            ...(incluyeTrading ? [{
              label: 'Trading mes',
              value: fmtM(data.trading?.mes?.resultado || 0),
              color: Number(data.trading?.mes?.resultado || 0) >= 0 ? '#059669' : '#dc2626',
              sub: `${data.trading?.mes?.operaciones || 0} ops · ${oculto ? '••%' : Number(data.trading?.mes?.rendimiento_pct || 0).toFixed(2) + '%'}`,
            }] : []),
            { label: 'Registros',  value: data.count,           sub: 'este mes' },
          ].map((k, i) => (
            <div key={i} style={{
              background: k.accent ? '#08293f' : '#fff',
              border: k.accent ? '1px solid rgba(0,184,227,0.3)'
                    : k.accentGreen !== undefined ? `1px solid ${k.accentGreen ? 'rgba(5,150,105,0.3)' : 'rgba(220,38,38,0.3)'}`
                    : '1px solid #e8eef2',
              padding: '14px 16px', borderRadius: 2
            }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: k.accent ? 'rgba(0,184,227,0.6)' : '#5a7a8a', marginBottom: 5 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1,
                color: k.accent ? '#00b8e3' : (k.color || '#08293f') }}>{k.value}</div>
              {k.delta !== null && k.delta !== undefined && (
                <div style={{ fontSize: 10, fontWeight: 600, marginTop: 3, color: k.delta >= 0 ? '#059669' : '#dc2626' }}>
                  {oculto ? '••%' : `${k.delta >= 0 ? '↑' : '↓'} ${Math.abs(k.delta)}%`} {incluyeAcademia && i===0 ? '' : (k.sub || '')}
                </div>
              )}
              {k.sub && k.delta === undefined && (
                <div style={{ fontSize: 10, color: k.accent ? 'rgba(0,184,227,0.5)' : '#b3b3b3', marginTop: 2 }}>{k.sub}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Metas del mes ── */}
      {(promedioAnt > 0 || mejorMes > 0) && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 14 }}>— Metas del mes</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(promedioAnt > 0 || data.mes_anterior?.ingresos > 0) && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#08293f' }}>Meta 1 — {meta1Label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: pctMeta1 >= 100 ? '#059669' : '#08293f' }}>{oculto ? '••%' : `${pctMeta1}%`}</span>
                </div>
                <div style={{ height: 10, background: '#f4f7fa', borderRadius: 5, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ width: `${pctMeta1}%`, height: '100%', background: pctMeta1 >= 100 ? '#059669' : '#00b8e3', borderRadius: 5, transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#5a7a8a' }}>
                  <span>Utilidad bruta: <strong style={{ color: '#08293f' }}>{fmtM(utilidadActual)}</strong></span>
                  {faltaMeta1 > 0
                    ? <span>Faltan <strong style={{ color: '#b45309' }}>{fmtM(faltaMeta1)}</strong> · Objetivo: {fmtM(Math.round(promedioAnt))}</span>
                    : <span style={{ color: '#059669', fontWeight: 600 }}>¡Meta superada! Objetivo: {fmtM(Math.round(promedioAnt))}</span>
                  }
                </div>
                {noFacturoUtilidadMes > 0 && (
                  <div style={{ marginTop: 5, fontSize: 10, color: '#b45309' }}>
                    Incluye comisión/no facturo {fmtM(noFacturoUtilidadMes)}. Para seguridad social se excluye el ingreso no facturado por mí.
                  </div>
                )}
              </div>
            )}
            {mejorMes > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#08293f' }}>Meta 2 — Superar mejor mes del año</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: pctMeta2 >= 100 ? '#059669' : '#08293f' }}>{oculto ? '••%' : `${pctMeta2}%`}</span>
                </div>
                <div style={{ height: 10, background: '#f4f7fa', borderRadius: 5, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ width: `${pctMeta2}%`, height: '100%', background: pctMeta2 >= 100 ? '#059669' : '#6366f1', borderRadius: 5, transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#5a7a8a' }}>
                  <span>Utilidad bruta: <strong style={{ color: '#08293f' }}>{fmtM(utilidadActual)}</strong></span>
                  {faltaMeta2 > 0
                    ? <span>Faltan <strong style={{ color: '#b45309' }}>{fmtM(faltaMeta2)}</strong> · Récord: {fmtM(mejorMes)}</span>
                    : <span style={{ color: '#059669', fontWeight: 600 }}>¡Nuevo récord! Anterior: {fmtM(mejorMes)}</span>
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Comparativo anual ── */}
      {hayComparativo && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 14 }}>
            — Comparativo {anio} vs {anio - 1} vs {anio - 2} <span style={{ fontSize: 9, fontWeight: 400, color: '#b3b3b3' }}>(mismo periodo: ene-{MESES_ABREV[mesesComparativo - 1]})</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr 1fr 1fr', gap: '4px 16px', alignItems: 'center', fontSize: 12 }}>
            {/* Header */}
            {['', String(anio), String(anio - 1), String(anio - 2), 'Var.', 'Dif. acum.'].map((h, i) => (
              <div key={i} style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#5a7a8a', paddingBottom: 8, borderBottom: '1px solid #e8eef2' }}>{h}</div>
            ))}
            {/* Ingresos totales */}
            {['Ingresos totales', fmtM(totalAct), fmtM(totalAnt), fmtM(totalAnt2), null, null].map((v, i) => {
              const varPct = i === 4 ? pct(totalAct, totalAnt) : null;
              const varTxt = i === 4 ? varDisplay(totalAct, totalAnt) : null;
              const diffTxt = i === 5 ? diffDisplay(totalAct, totalAnt) : null;
              return (
                <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid #f4f7fa', fontWeight: i===0?400:700, color: i===0?'#5a7a8a':'#08293f' }}>
                  {i === 4
                    ? varTxt !== null ? <span style={{ color: varPct >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{oculto ? '••% (••••)' : varTxt}</span> : '—'
                    : i === 5
                      ? <span style={{ color: totalAct - totalAnt >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{oculto ? '••••' : diffTxt}</span>
                      : v}
                </div>
              );
            })}
            {/* Utilidad bruta totales */}
            {['Utilidad bruta total', fmtM(utilAct), fmtM(utilAnt), fmtM(utilAnt2), null, null].map((v, i) => {
              const varPct = i === 4 ? pct(utilAct, utilAnt) : null;
              const varTxt = i === 4 ? varDisplay(utilAct, utilAnt) : null;
              const diffTxt = i === 5 ? diffDisplay(utilAct, utilAnt) : null;
              return (
                <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid #f4f7fa', fontWeight: i===0?400:700, color: i===0?'#5a7a8a':i===1?'#059669':'#08293f' }}>
                  {i === 4
                    ? varTxt !== null ? <span style={{ color: varPct >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{oculto ? '••% (••••)' : varTxt}</span> : '—'
                    : i === 5
                      ? <span style={{ color: utilAct - utilAnt >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{oculto ? '••••' : diffTxt}</span>
                      : v}
                </div>
              );
            })}
            {/* Mejor mes */}
            {['Mejor mes (utilidad)',
              mejorMesAct ? `${mejorMesAct.mes?.split(' ')[0]} ${fmtM(utilidadTotalMes(mejorMesAct))}` : '—',
              mejorMesAnt ? `${mejorMesAnt.mes?.split(' ')[0]} ${fmtM(utilidadTotalMes(mejorMesAnt))}` : '—',
              mejorMesAnt2 ? `${mejorMesAnt2.mes?.split(' ')[0]} ${fmtM(utilidadTotalMes(mejorMesAnt2))}` : '—',
              '', ''].map((v, i) => (
              <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid #f4f7fa', fontWeight: i===0?400:600, color: i===0?'#5a7a8a':'#08293f', fontSize: i===0?12:11 }}>{v}</div>
            ))}
            {/* Promedio mensual */}
            {['Promedio mensual (utilidad)', fmtM(promedioAct), fmtM(promedioAntVal), fmtM(promedioAnt2Val), null, null].map((v, i) => {
              const varPct = i === 4 ? pct(promedioAct, promedioAntVal) : null;
              const varTxt = i === 4 ? varDisplay(promedioAct, promedioAntVal) : null;
              const diffTxt = i === 5 ? diffDisplay(utilAct, utilAnt) : null;
              return (
                <div key={i} style={{ padding: '7px 0', fontWeight: i===0?400:600, color: i===0?'#5a7a8a':'#08293f' }}>
                  {i === 4
                    ? varTxt !== null ? <span style={{ color: varPct >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{oculto ? '••% (••••)' : varTxt}</span> : '—'
                    : i === 5
                      ? <span style={{ color: utilAct - utilAnt >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{oculto ? '••••' : diffTxt}</span>
                      : v}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: '#b3b3b3' }}>
            ⚠ Comparativo solo de ingresos y utilidad bruta (registros). Gastos históricos no disponibles.
          </div>
        </div>
      )}

      {/* ── Estados ── */}
      <div className="card">
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 12 }}>— Estados {anio}</div>
        <div className="estado-grid">
          {['PAGADO','PENDIENTE','FACTURADO'].map(e => {
            const st = ESTADO_STYLE[e] || {};
            const d  = estadoMap[e];
            return (
              <div key={e} style={{ background: st.bg, padding: '12px 14px', borderRadius: 2 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: st.color, marginBottom: 5 }}>
                  {e === 'FACTURADO' ? 'FACTURADO PENDIENTE DE PAGO' : e}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#08293f' }}>{d?.count || 0}</div>
                <div style={{ fontSize: 11, color: '#5a7a8a', marginTop: 2 }}>{fmtM(d?.valor)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Seguridad Social Independiente ── */}
      {(() => {
        const ibcData       = getMes(data.porMes, ibcMes);
        const noFacturoMes  = parseFloat(ibcData.no_facturo_total) || 0;
        const sinFacturaMes = parseFloat(ibcData.sin_factura_total) || 0;
        const facturadoSSMes = parseFloat(ibcData.facturado_ss_total) || 0;
        const ingresoTotal  = parseFloat(ibcData.valor) || 0;
        const ingresoBrutoTotal = ingresoTotal + noFacturoMes;
        const ingresoBruto  = facturadoSSMes || (ingresoTotal - sinFacturaMes);
        const costos     = ingresoBruto * 0.632;
        const baseNeta   = ingresoBruto * 0.368;
        const ibc        = baseNeta * 0.40;
        const salud      = Math.round(ibc * 0.125);
        const pension    = Math.round(ibc * 0.16);
        const arl        = Math.round(ibc * 0.00522);
        const total      = salud + pension + arl;
        const mesNombre  = MESES_FULL[ibcMes - 1];
        return (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Seguridad social independiente</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#5a7a8a' }}>Año {anio} — Mes:</span>
                <select value={ibcMes} onChange={e => setIbcMes(Number(e.target.value))} className="input" style={{ width: 'auto', fontSize: 11, padding: '3px 8px' }}>
                  {MESES_FULL.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
            </div>

            {/* Cálculo paso a paso */}
            <div style={{ background: '#f4f7fa', borderRadius: 2, padding: '12px 14px', marginBottom: 12, fontSize: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 7 }}>
                <span style={{ color: '#5a7a8a' }}>Ingreso total registrado — {mesNombre} {anio}</span>
                <span style={{ fontWeight: 700, color: '#08293f', textAlign: 'right' }}>{oculto ? '$ ••••' : fmt(ingresoBrutoTotal)}</span>

                {facturadoSSMes > 0 && Math.round(facturadoSSMes) !== Math.round(ingresoTotal - sinFacturaMes) && <>
                  <span style={{ color: '#6366f1' }}>Base facturada para SS</span>
                  <span style={{ fontWeight: 700, color: '#6366f1', textAlign: 'right' }}>{oculto ? '$ ••••' : fmt(facturadoSSMes)}</span>
                </>}

                {noFacturoMes > 0 && <>
                  <span style={{ color: '#b45309' }}>— No facturado por mí</span>
                  <span style={{ fontWeight: 600, color: '#b45309', textAlign: 'right' }}>{oculto ? '$ ••••' : '− ' + fmt(noFacturoMes)}</span>
                </>}
                {sinFacturaMes > 0 && <>
                  <span style={{ color: '#b45309' }}>— Sin factura</span>
                  <span style={{ fontWeight: 600, color: '#b45309', textAlign: 'right' }}>{oculto ? '$ ••••' : '− ' + fmt(sinFacturaMes)}</span>
                </>}
                {(noFacturoMes > 0 || sinFacturaMes > 0) && <>
                  <div style={{ gridColumn: '1/-1', height: 1, background: '#e8eef2', margin: '2px 0' }} />
                  <span style={{ color: '#5a7a8a', fontWeight: 600 }}>Ingreso base SS</span>
                  <span style={{ fontWeight: 700, color: '#08293f', textAlign: 'right' }}>{oculto ? '$ ••••' : fmt(ingresoBruto)}</span>
                </>}

                <span style={{ color: '#5a7a8a' }}>— Costos asociados <span style={{ color: '#dc2626', fontWeight: 600 }}>63.2%</span></span>
                <span style={{ fontWeight: 700, color: '#dc2626', textAlign: 'right' }}>{oculto ? '$ ••••' : '− ' + fmt(costos)}</span>

                <div style={{ gridColumn: '1/-1', height: 1, background: '#e8eef2', margin: '2px 0' }} />

                <span style={{ color: '#5a7a8a' }}>Base neta <span style={{ color: '#5a7a8a', fontWeight: 400 }}>(36.8%)</span></span>
                <span style={{ fontWeight: 700, color: '#08293f', textAlign: 'right' }}>{oculto ? '$ ••••' : fmt(baseNeta)}</span>

                <span style={{ color: '#5a7a8a' }}>× 40% → <strong style={{ color: '#6366f1' }}>IBC</strong></span>
                <span style={{ fontWeight: 800, color: '#6366f1', fontSize: 14, textAlign: 'right' }}>{oculto ? '$ ••••' : fmt(ibc)}</span>
              </div>
            </div>

            {/* Aportes */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
              {[
                { label: 'Salud',        pct: '12.5%',  value: salud,   color: '#00b8e3' },
                { label: 'Pensión',      pct: '16%',    value: pension, color: '#6366f1' },
                { label: 'ARL',          pct: '0.522%', value: arl,     color: '#b45309' },
                { label: 'Total aportes',pct: '',       value: total,   color: '#059669' },
              ].map((k, i) => (
                <div key={i} style={{ padding: '10px 12px', background: '#fff', border: `1px solid rgba(0,0,0,0.06)`, borderRadius: 2, borderTop: `3px solid ${k.color}` }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 4 }}>
                    {k.label}{k.pct && <span style={{ color: k.color, marginLeft: 4 }}>{k.pct}</span>}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: k.color }}>{oculto ? '$ ••••' : fmt(k.value)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Comparativo de utilidad ── */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Utilidad bruta y promedio</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select value={utilidadChartValor} onChange={e => setUtilidadChartValor(e.target.value)} className="input" style={{ width: 'auto', fontSize: 11, padding: '4px 8px' }}>
              <option value="ambos">Utilidad + promedio</option>
              <option value="bruta">Solo utilidad bruta</option>
              <option value="promedio">Solo promedio</option>
            </select>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ANIOS.map(year => {
                const active = utilidadChartYears.includes(year);
                return (
                  <button key={year} type="button" onClick={() => toggleUtilidadYear(year)} style={{
                    padding: '5px 9px',
                    borderRadius: 2,
                    border: active ? '1px solid #00b8e3' : '1px solid #e8eef2',
                    background: active ? 'rgba(0,184,227,0.08)' : '#fff',
                    color: active ? '#08293f' : '#5a7a8a',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit'
                  }}>
                    {year}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ filter: oculto ? 'blur(5px)' : 'none', transition: 'filter 0.3s', pointerEvents: oculto ? 'none' : 'auto' }}>
          <Line data={utilidadComparativaData} options={{ ...chartOpts, scales: {
            x: { grid: { display: false }, ticks: { font: { family: 'Space Grotesk', size: 10 }, color: '#5a7a8a' } },
            y: { grid: { color: '#f4f7fa' }, ticks: { callback: v => fmtShort(v), font: { family: 'Space Grotesk', size: 10 }, color: '#5a7a8a' } }
          }}} />
        </div>
        <div style={{ marginTop: 8, fontSize: 10, color: '#b3b3b3' }}>
          Promedio calculado con los meses que tienen utilidad registrada en cada año.
        </div>
      </div>

      {/* ── Gráficas ── */}
      <div className="charts-grid" style={{ filter: oculto ? 'blur(5px)' : 'none', transition: 'filter 0.3s', pointerEvents: oculto ? 'none' : 'auto' }}>
        <div className="card">
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 12 }}>
            — Ingresos propios + no facturo / Utilidad total por mes {dataAnt?.porMes?.length ? `(línea = utilidad total ${anio-1})` : ''}
          </div>
          <Bar data={barData} plugins={[lineOnTopPlugin]} options={{ ...chartOpts, scales: {
            x: { grid: { display: false }, ticks: { font: { family: 'Space Grotesk', size: 10 }, color: '#5a7a8a' } },
            y: { grid: { color: '#f4f7fa' }, ticks: { callback: v => fmtShort(v), font: { family: 'Space Grotesk', size: 10 }, color: '#5a7a8a' } }
          }}} />
        </div>

        <div className="card">
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 12 }}>— Top clientes</div>
          <Doughnut data={clienteData} options={chartOpts} />
        </div>

        <div className="card">
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 12 }}>— FIJO vs PARTICULAR</div>
          <Pie data={tipoData} options={chartOpts} />
        </div>

        <div className="card">
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 12 }}>— Top servicios</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.porServicio.map((s, i) => {
              const max = data.porServicio[0]?.count || 1;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#5a7a8a', width: 110, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{s.servicio}</span>
                  <div style={{ flex: 1, height: 6, background: '#f4f7fa', borderRadius: 1, overflow: 'hidden' }}>
                    <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', background: '#00b8e3' }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#08293f', width: 18, textAlign: 'right' }}>{s.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .anual-grid  { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; }
        .kpi-grid    { display: grid; grid-template-columns: repeat(7,1fr); gap: 8px; }
        .estado-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; }
        .charts-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        @media (max-width: 1200px) { .kpi-grid { grid-template-columns: repeat(4,1fr); } }
        @media (max-width: 900px)  { .kpi-grid { grid-template-columns: repeat(3,1fr); } }
        @media (max-width: 768px) {
          .anual-grid  { grid-template-columns: 1fr; }
          .kpi-grid    { grid-template-columns: repeat(2,1fr); }
          .estado-grid { grid-template-columns: repeat(2,1fr); }
          .charts-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
